/**
 * ManuallyAddPlayerModal View Component
 *
 * Renders the modal form for manually adding or editing a player.
 * Includes required fields (First Name, Last Name, Positions via dropdown MultiSelect) and
 * optional fields (Current Team with autocomplete, Nationality, Date of Birth).
 * Title, description, and submit labels are dynamic props controlled by the Container.
 * Uses gluestack Modal's native `avoidKeyboard` prop to shift the modal above the
 * on-screen keyboard on both iOS and Android (SE-3982).
 * Uses ModalBody's built-in ScrollView instead of a nested one.
 * @module components/ManuallyAddPlayerModal
 */

import { AlertCircleIcon, XIcon } from "lucide-react-native";
import { memo } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldErrors,
} from "react-hook-form";
import {
  IManuallyAddPlayerForm,
  ManuallyAddPlayerModalViewProps,
} from "@/types/manuallyAddPlayerModalTypes";

import { renderCurrentTeamField } from "@/components/organisms/ManuallyAddPlayerModal/CurrentTeamField";
import CustomSelect from "@/components/molecules/CustomSelect";
import DateInput from "@/components/molecules/DateInput";
import PositionMultiSelect from "./PositionMultiSelect";
import { Button } from "@/components/atoms/Button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/atoms/FormControl";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { IOptionSelect } from "@/types/customSelectTypes";
import { ifWeb } from "@/utils/platform";

/**
 * Gets the modal background class based on theme
 * @param isDark - Whether dark mode is active
 */
const getModalBgClass = (isDark: boolean): string =>
  isDark ? "bg-surface-raised" : "bg-white";

/**
 * Factory for rendering the First Name text input field
 * @param isSubmitting - Whether the form is submitting
 * @param errors - Form validation errors
 */
const renderFirstNameField = (
  isSubmitting: boolean,
  errors: FieldErrors<IManuallyAddPlayerForm>
) =>
  function FirstNameField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IManuallyAddPlayerForm, "firstName">;
  }) {
    return (
      <FormControl
        size="md"
        isInvalid={!isSubmitting && !!errors.firstName}
        isRequired
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>First Name</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border"
          size="xl"
          field={{
            placeholder: "Enter first name",
            type: "text",
            value: value ?? "",
            keyboardType: "default",
            inputMode: "text",
            autoCorrect: false,
            autoComplete: "given-name",
            textContentType: "givenName",
            autoCapitalize: "words",
            returnKeyLabel: "Next",
            enterKeyHint: "next",
            onChangeText: onChange,
            onBlur,
            className: "font-body",
            testID: "manual-player-first-name",
          }}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.firstName?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory for rendering the Last Name text input field
 * @param isSubmitting - Whether the form is submitting
 * @param errors - Form validation errors
 */
const renderLastNameField = (
  isSubmitting: boolean,
  errors: FieldErrors<IManuallyAddPlayerForm>
) =>
  function LastNameField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IManuallyAddPlayerForm, "lastName">;
  }) {
    return (
      <FormControl
        size="md"
        isInvalid={!isSubmitting && !!errors.lastName}
        isRequired
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Last Name</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border"
          size="xl"
          field={{
            placeholder: "Enter last name",
            type: "text",
            value: value ?? "",
            keyboardType: "default",
            inputMode: "text",
            autoCorrect: false,
            autoComplete: "family-name",
            textContentType: "familyName",
            autoCapitalize: "words",
            returnKeyLabel: "Next",
            enterKeyHint: "next",
            onChangeText: onChange,
            onBlur,
            className: "font-body",
            testID: "manual-player-last-name",
          }}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.lastName?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory for rendering the position multi-select dropdown field.
 * Delegates to platform-specific PositionMultiSelect (react-select on web,
 * react-native-element-dropdown on native).
 * Error is only shown after the user has attempted to submit the form,
 * since showing "Select at least one position" before any interaction is confusing.
 * @param positionOptions - Available position options
 * @param isDark - Whether dark mode is active
 * @param errors - Form validation errors
 * @param isSubmitting - Whether the form is submitting
 * @param isSubmitted - Whether the form has been submitted at least once
 */
const renderPositionDropdownField = (
  positionOptions: readonly IOptionSelect[],
  isDark: boolean,
  errors: FieldErrors<IManuallyAddPlayerForm>,
  isSubmitting: boolean,
  isSubmitted: boolean
) =>
  function PositionDropdownField({
    field: { onChange, value },
  }: {
    field: ControllerRenderProps<IManuallyAddPlayerForm, "positionIds">;
  }) {
    return (
      <FormControl
        size="md"
        isInvalid={!isSubmitting && isSubmitted && !!errors.positionIds}
        isRequired
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Positions</FormControlLabelText>
        </FormControlLabel>
        <PositionMultiSelect
          options={positionOptions}
          value={value ?? []}
          onChange={onChange}
          isDark={isDark}
          isDisabled={isSubmitting}
          testID="manual-player-positions"
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.positionIds?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory for rendering the Nationality dropdown field
 * @param options - Nationality dropdown options
 */
const renderNationalityField = (options: readonly IOptionSelect[]) =>
  function NationalityField({
    field: { onChange, value },
  }: {
    field: ControllerRenderProps<IManuallyAddPlayerForm, "nationalityId">;
  }) {
    return (
      <FormControl size="md">
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Nationality</FormControlLabelText>
        </FormControlLabel>
        <CustomSelect
          options={[...options]}
          defaultValue={value}
          placeholder="Select country"
          onChangeValue={onChange}
        />
      </FormControl>
    );
  };

/**
 * Factory for rendering the Date of Birth field with cross-platform DateInput
 * @param isSubmitting - Whether the form is submitting
 * @param errors - Form validation errors
 * @param isDark - Whether dark theme is active
 */
const renderDateOfBirthField = (
  isSubmitting: boolean,
  errors: FieldErrors<IManuallyAddPlayerForm>,
  isDark: boolean
) =>
  function DateOfBirthField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IManuallyAddPlayerForm, "dateOfBirth">;
  }) {
    return (
      <FormControl size="md" isInvalid={!isSubmitting && !!errors.dateOfBirth}>
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Date of Birth</FormControlLabelText>
        </FormControlLabel>
        <DateInput
          value={value ?? ""}
          onChange={onChange}
          onBlur={onBlur}
          testID="manual-player-dob"
          isDark={isDark}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.dateOfBirth?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/** Config for a numeric form field */
interface NumericFieldConfig {
  readonly name: keyof IManuallyAddPlayerForm;
  readonly label: string;
  readonly placeholder: string;
  readonly testID: string;
  readonly isRequired?: boolean;
}

/**
 * Generic factory for numeric input fields (academy mode).
 * Replaces individual Age/Salary/Valuation/Contract factories.
 * @param config - Field configuration (name, label, placeholder, testID, isRequired)
 * @param isSubmitting - Whether the form is submitting
 * @param errors - Form validation errors
 */
const renderNumericField = (
  config: NumericFieldConfig,
  isSubmitting: boolean,
  errors: FieldErrors<IManuallyAddPlayerForm>
) =>
  function NumericField({
    field: { onChange, onBlur, value },
  }: {
    /** Academy fields share a single generic factory, so the field type must be broad */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- academy fields share a single generic factory
    field: ControllerRenderProps<IManuallyAddPlayerForm, any>;
  }) {
    return (
      <FormControl
        size="md"
        isInvalid={!isSubmitting && !!errors[config.name]}
        isRequired={config.isRequired}
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>{config.label}</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border"
          size="xl"
          field={{
            placeholder: config.placeholder,
            type: "text",
            value: value ?? "",
            keyboardType: "number-pad",
            inputMode: "numeric",
            onChangeText: onChange,
            onBlur,
            className: "font-body",
            testID: config.testID,
          }}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors[config.name]?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

const AGE_FIELD: NumericFieldConfig = {
  name: "age",
  label: "Age",
  placeholder: "e.g. 17",
  testID: "manual-player-age",
  isRequired: true,
};
const SALARY_FIELD: NumericFieldConfig = {
  name: "grossAnnualSalary",
  label: "Gross Annual Salary",
  placeholder: "e.g. 25000",
  testID: "manual-player-salary",
};
const VALUATION_FIELD: NumericFieldConfig = {
  name: "marketValuation",
  label: "Market Valuation",
  placeholder: "e.g. 500000",
  testID: "manual-player-market-valuation",
};
const CONTRACT_FIELD: NumericFieldConfig = {
  name: "contractMonthsRemaining",
  label: "Contract Months Remaining",
  placeholder: "e.g. 24",
  testID: "manual-player-contract-months",
};

/**
 * Pure view component for the Manually Add Player modal form
 * @param props - Component props
 * @param props.isOpen - Whether the modal is visible
 * @param props.isDark - Whether dark theme is active
 * @param props.title - Modal heading text
 * @param props.description - Modal description text
 * @param props.submitLabel - Submit button label
 * @param props.submitLoadingLabel - Submit button label while loading
 * @param props.isValid - Whether all required form fields are valid
 * @param props.isSubmitting - Whether form submission is in progress
 * @param props.isSubmitted - Whether the form has been submitted at least once
 * @param props.control - React Hook Form control for binding fields
 * @param props.errors - Validation errors by field name
 * @param props.positionOptions - Position options for multi-select dropdown
 * @param props.nationalityOptions - Nationality dropdown options
 * @param props.teamSuggestions - Team autocomplete suggestion options
 * @param props.teamSearchText - Current team search text
 * @param props.onClose - Close handler
 * @param props.onSubmit - Submit handler
 * @param props.onTeamSearchChange - Team search text change handler
 * @param props.onTeamSuggestionSelect - Team suggestion selection handler
 * @param props.isAcademy - Whether the modal is in academy mode (shows extended fields)
 */
const ManuallyAddPlayerModalView = ({
  isOpen,
  isDark,
  title,
  description,
  submitLabel,
  submitLoadingLabel,
  isValid,
  isSubmitting,
  isSubmitted,
  control,
  errors,
  positionOptions,
  nationalityOptions,
  teamSuggestions,
  teamSearchText,
  onClose,
  onSubmit,
  onTeamSearchChange,
  onTeamSuggestionSelect,
  isAcademy,
}: ManuallyAddPlayerModalViewProps) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    closeOnOverlayClick={false}
    avoidKeyboard
    size="lg"
  >
    <ModalBackdrop />
    <ModalContent
      className={`${getModalBgClass(isDark)} max-h-3/4 ${ifWeb("max-w-lg", "")}`}
    >
      <ModalHeader className="border-b border-surface-muted">
        <Stack className="flex-1 pr-2">
          <Heading
            variant="title-lg"
            className={isDark ? "text-white" : "text-content-primary"}
          >
            {title}
          </Heading>
          <Text variant="body" className="text-content-muted">
            {description}
          </Text>
        </Stack>
        <Pressable
          onPress={onClose}
          accessibilityLabel="Close modal"
          accessibilityRole="button"
          testID="manual-player-close"
        >
          <Icon as={XIcon} size="md" className="text-content-muted" />
        </Pressable>
      </ModalHeader>

      <ModalBody
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="py-4"
        className={ifWeb("scrollbar-hide", "")}
      >
        <Stack className="gap-3">
          <Stack direction="horizontal" className="gap-3">
            <Stack className="flex-1">
              <Controller
                name="firstName"
                control={control}
                render={renderFirstNameField(isSubmitting, errors)}
              />
            </Stack>
            <Stack className="flex-1">
              <Controller
                name="lastName"
                control={control}
                render={renderLastNameField(isSubmitting, errors)}
              />
            </Stack>
          </Stack>

          <Controller
            name="positionIds"
            control={control}
            render={renderPositionDropdownField(
              positionOptions,
              isDark,
              errors,
              isSubmitting,
              isSubmitted
            )}
          />

          {isAcademy && (
            <>
              <Stack direction="horizontal" className="gap-3">
                <Stack className="flex-1">
                  <Controller
                    name="age"
                    control={control}
                    render={renderNumericField(AGE_FIELD, isSubmitting, errors)}
                  />
                </Stack>
                <Stack className="flex-1">
                  <Controller
                    name="grossAnnualSalary"
                    control={control}
                    render={renderNumericField(
                      SALARY_FIELD,
                      isSubmitting,
                      errors
                    )}
                  />
                </Stack>
              </Stack>
              <Stack direction="horizontal" className="gap-3">
                <Stack className="flex-1">
                  <Controller
                    name="marketValuation"
                    control={control}
                    render={renderNumericField(
                      VALUATION_FIELD,
                      isSubmitting,
                      errors
                    )}
                  />
                </Stack>
                <Stack className="flex-1">
                  <Controller
                    name="contractMonthsRemaining"
                    control={control}
                    render={renderNumericField(
                      CONTRACT_FIELD,
                      isSubmitting,
                      errors
                    )}
                  />
                </Stack>
              </Stack>
            </>
          )}

          <FormControl size="md">
            <FormControlLabel className="pb-1">
              <FormControlLabelText>Current Team</FormControlLabelText>
            </FormControlLabel>
            <Controller
              name="currentTeam"
              control={control}
              render={renderCurrentTeamField(
                isDark,
                teamSearchText,
                teamSuggestions,
                onTeamSearchChange,
                onTeamSuggestionSelect
              )}
            />
          </FormControl>

          <Stack direction="horizontal" className="gap-3">
            <Stack className="flex-1">
              <Controller
                name="nationalityId"
                control={control}
                render={renderNationalityField(nationalityOptions)}
              />
            </Stack>
            <Stack className="flex-1">
              <Controller
                name="dateOfBirth"
                control={control}
                render={renderDateOfBirthField(isSubmitting, errors, isDark)}
              />
            </Stack>
          </Stack>
        </Stack>
      </ModalBody>

      <ModalFooter className="border-t border-surface-muted p-4">
        <Stack
          direction="horizontal"
          className="web:justify-end w-full gap-3 native:flex-1"
        >
          <Button
            label="Cancel"
            variant="outline"
            labelTone="muted"
            labelVariant="title-sm"
            onPress={onClose}
            className="h-10 rounded-sm border-surface-muted px-6"
            testID="manual-player-cancel"
            accessibilityLabel="Cancel"
          />
          <Button
            label={isSubmitting ? submitLoadingLabel : submitLabel}
            labelTone="on-accent"
            labelVariant="title-sm"
            isDisabled={!isValid || isSubmitting}
            onPress={onSubmit}
            className="h-10 rounded-sm bg-accent-primary px-6 data-[disabled=true]:opacity-40 native:flex-1"
            testID="manual-player-submit"
            accessibilityLabel={submitLabel}
          />
        </Stack>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

ManuallyAddPlayerModalView.displayName = "ManuallyAddPlayerModalView";

export default memo(ManuallyAddPlayerModalView);
